import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { SoundManager } from '@/systems/SoundManager';
import { AchievementManager } from '@/systems/AchievementManager';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { EventBus, GameEvents } from '@/events/EventBus';
import { TECH, createTechButton } from '@/ui/TechUI';
import { TIER_COLORS, CATEGORY_ORDER, AchievementDef, AchievementReward } from '@/config/AchievementDefs';
import { PERK_DEFS, PERK_CATEGORY_LABELS, PERK_CATEGORY_ICONS, PerkCategory } from '@/config/PerkDefs';
import { devAchieveOnClick } from '@/systems/DevConsole';
import { t } from '@/i18n/I18n';

export class StatsAchievementPanel {
  private statsPanel?: Phaser.GameObjects.Container;
  private statsOpen = false;
  private _achieveQueue: { id: string; badge: string; def: AchievementDef }[] = [];
  private _achieveNotifying = false;
  private _statsActiveTab: 'achievements' | 'collection' = 'achievements';

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean { return this.statsOpen; }
  get achieveQueue(): { id: string; badge: string; def: AchievementDef }[] { return this._achieveQueue; }
  get isNotifying(): boolean { return this._achieveNotifying; }

  open(): void {
    if (this.statsOpen) { this.close(); return; }
    this.statsOpen = true;

    // Run achievement check to ensure latest state
    AchievementManager.checkAll();

    const pw = 700, ph = 680;
    const panel = this.scene.add.container(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2).setDepth(25);
    this.statsPanel = panel;

    // Full-screen backdrop
    const backdrop = this.scene.add.rectangle(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.3)
      .setInteractive();
    backdrop.on('pointerdown', () => { this.close(); });
    panel.add(backdrop);

    // Panel background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0a0a1e, 0.95);
    bg.fillRoundedRect(-pw / 2, -ph / 2, pw, ph, 8);
    bg.lineStyle(1.5, 0xffaa44, 0.6);
    bg.strokeRoundedRect(-pw / 2, -ph / 2, pw, ph, 8);
    panel.add(bg);

    // Block click-through
    const blocker = this.scene.add.rectangle(0, 0, pw, ph, 0x000000, 0).setInteractive();
    panel.add(blocker);

    // Title
    panel.add(this.scene.add.text(0, -ph / 2 + 18, t('achievement.title'), {
      fontSize: '18px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    // ── Tab bar ──
    const tabY = -ph / 2 + 42;
    const tabW = 140, tabH = 26, tabGap = 10;
    const tabs: { key: 'achievements' | 'collection'; label: string; icon: string }[] = [
      { key: 'achievements', label: t('stats.achievements'), icon: '\ud83c\udfc5' },
      { key: 'collection', label: t('stats.collection'), icon: '\ud83c\udfc6' },
    ];
    const tabContainer = this.scene.add.container(0, 0);
    panel.add(tabContainer);

    // ── Scrollable viewport (below tabs) ──
    const viewTop = -ph / 2 + 72;
    const viewBottom = ph / 2 - 50;
    const viewH = viewBottom - viewTop;
    const viewW = pw - 20;

    const maskShape = this.scene.make.graphics({ x: 0, y: 0 });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(
      CANVAS_WIDTH / 2 - viewW / 2, CANVAS_HEIGHT / 2 + viewTop,
      viewW, viewH,
    );
    const mask = maskShape.createGeometryMask();

    // Content container -- rebuilt on tab switch
    let content = this.scene.add.container(0, 0);
    content.setMask(mask);
    panel.add(content);

    // Scroll state
    let scrollY = 0;
    let maxScroll = 0;

    // ── Build tab buttons ──
    const buildTabs = () => {
      tabContainer.removeAll(true);
      const totalW = tabs.length * tabW + (tabs.length - 1) * tabGap;
      let tx = -totalW / 2 + tabW / 2;
      for (const tab of tabs) {
        const isActive = this._statsActiveTab === tab.key;
        const unclaimedBadge = tab.key === 'achievements' ? AchievementManager.getUnclaimedCount()
          : tab.key === 'collection' ? ProgressionManager.getUnseenPerkCount() : 0;

        const tabGfx = this.scene.add.graphics();
        tabGfx.fillStyle(isActive ? 0x1a1a3a : 0x0e0e1e, 0.95);
        tabGfx.fillRoundedRect(tx - tabW / 2, tabY - tabH / 2, tabW, tabH, 4);
        tabGfx.lineStyle(1, isActive ? 0xffaa44 : 0x333355, isActive ? 0.8 : 0.4);
        tabGfx.strokeRoundedRect(tx - tabW / 2, tabY - tabH / 2, tabW, tabH, 4);
        tabContainer.add(tabGfx);

        const labelStr = `${tab.icon} ${tab.label}`;
        const tabLabel = this.scene.add.text(tx, tabY, labelStr, {
          fontSize: '10px', color: isActive ? '#ffaa44' : '#667788', fontFamily: 'monospace', fontStyle: isActive ? 'bold' : 'normal',
        }).setOrigin(0.5, 0.5);
        tabContainer.add(tabLabel);

        // Unclaimed badge on achievements tab
        if (unclaimedBadge > 0) {
          const badgeCircle = this.scene.add.graphics();
          badgeCircle.fillStyle(0xff4444, 1);
          badgeCircle.fillCircle(tx + tabW / 2 - 8, tabY - tabH / 2 + 4, 8);
          tabContainer.add(badgeCircle);
          const badgeNum = this.scene.add.text(tx + tabW / 2 - 8, tabY - tabH / 2 + 4, `${unclaimedBadge}`, {
            fontSize: '8px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5, 0.5);
          tabContainer.add(badgeNum);
        }

        // Click zone
        const hitZone = this.scene.add.rectangle(tx, tabY, tabW, tabH, 0x000000, 0)
          .setInteractive({ useHandCursor: true });
        hitZone.on('pointerdown', () => {
          if (this._statsActiveTab === tab.key) return;
          SoundManager.play('click');
          this._statsActiveTab = tab.key;
          buildTabs();
          rebuildContent();
        });
        tabContainer.add(hitZone);

        tx += tabW + tabGap;
      }
    };

    // ── Rebuild content for active tab ──
    const rebuildContent = (keepScroll = false) => {
      buildTabs(); // refresh tab badges (unclaimed count)
      const prevScroll = scrollY;
      content.removeAll(true);

      if (this._statsActiveTab === 'achievements') {
        maxScroll = this.buildAchievementsTab(content, pw, viewTop, viewH, panel, rebuildContent);
      } else {
        maxScroll = this.buildCollectionTab(content, pw, viewTop, viewH);
        // Mark all perks as seen when viewing collection
        ProgressionManager.markAllPerksSeen();
      }

      if (keepScroll) {
        scrollY = Phaser.Math.Clamp(prevScroll, -maxScroll, 0);
      } else {
        scrollY = 0;
      }
      content.y = scrollY;
    };

    // Scroll handler
    blocker.on('wheel', (_p: Phaser.Input.Pointer, _dx: number, dy: number, dz: number) => {
      if (maxScroll <= 0) return;
      const delta = Math.abs(dz) > Math.abs(dy) ? dz : dy;
      scrollY = Phaser.Math.Clamp(scrollY - delta * 0.5, -maxScroll, 0);
      content.y = scrollY;
    });

    buildTabs();
    rebuildContent();

    // Close button
    const closeBtn = createTechButton(this.scene, 0, ph / 2 - 35, t('achievement.close'), {
      onClick: () => { SoundManager.play('click'); this.close(); },
      width: 100, height: 32, fontSize: '13px',
    });
    panel.add(closeBtn);

    // ESC to close
    const escKey = this.scene.input.keyboard?.addKey('ESC');
    const escHandler = () => { SoundManager.play('click'); this.close(); };
    escKey?.on('down', escHandler);
    panel.once('destroy', () => { maskShape.destroy(); escKey?.off('down', escHandler); });
  }

  /** Build the Achievements tab content. Returns maxScroll. */
  private buildAchievementsTab(content: Phaser.GameObjects.Container, pw: number, viewTop: number, viewH: number, panel: Phaser.GameObjects.Container, rebuildContent?: (keepScroll?: boolean) => void): number {
    let cy = viewTop + 10;
    const allProgress = AchievementManager.getAllProgress();

    // ── Total Completion % ──
    {
      const totalRatio = allProgress.reduce((sum, p) => sum + Math.min(1, p.currentValue / p.def.threshold), 0);
      const completionPct = Math.round((totalRatio / allProgress.length) * 100);
      const pctStr = `${completionPct}%`;

      const RANK_THRESHOLDS = [100, 95, 90, 85, 80, 75, 70, 65, 60, 55, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5, 0];
      const rankThreshold = RANK_THRESHOLDS.find(th => completionPct >= th) ?? 0;
      const rankTitle = t(`achievement.rank.${rankThreshold}`);

      const compBarX = -pw / 2 + 60;
      const compBarW = pw - 120;
      const compBarH = 12;

      content.add(this.scene.add.text(-pw / 2 + 20, cy, t('achievement.completion'), {
        fontSize: '11px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0, 0.5));

      const rankColor = completionPct >= 100 ? '#ffdd44' : completionPct >= 50 ? '#88ddaa' : '#7799bb';
      content.add(this.scene.add.text(0, cy, `\u00ab ${rankTitle} \u00bb`, {
        fontSize: '11px', color: rankColor, fontFamily: 'monospace', fontStyle: 'italic',
      }).setOrigin(0.5, 0.5));

      content.add(this.scene.add.text(pw / 2 - 20, cy, pctStr, {
        fontSize: '13px', color: completionPct >= 100 ? '#ffdd44' : '#aabbcc', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(1, 0.5));

      cy += 14;

      const compGfx = this.scene.add.graphics();
      compGfx.fillStyle(0x222244, 0.6);
      compGfx.fillRoundedRect(compBarX, cy - compBarH / 2, compBarW, compBarH, 3);
      const compColor = completionPct >= 100 ? 0xffdd44 : completionPct >= 50 ? 0x44aa88 : 0x4488aa;
      compGfx.fillStyle(compColor, 0.8);
      compGfx.fillRoundedRect(compBarX, cy - compBarH / 2, Math.max(2, compBarW * (completionPct / 100)), compBarH, 3);
      content.add(compGfx);

      cy += 14;
    }

    // ── Unclaimed rewards summary ──
    const unclaimedCount = AchievementManager.getUnclaimedCount();
    if (unclaimedCount > 0) {
      const unclaimedText = this.scene.add.text(0, cy, `\ud83c\udf81 ${unclaimedCount} unclaimed reward${unclaimedCount > 1 ? 's' : ''}`, {
        fontSize: '11px', color: '#ffdd44', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5, 0.5);
      content.add(unclaimedText);
      this.scene.tweens.add({ targets: unclaimedText, alpha: { from: 1, to: 0.5 }, yoyo: true, repeat: -1, duration: 800 });
      cy += 18;
    } else {
      cy += 6;
    }

    const achieveLeft = -pw / 2 + 20;
    const rewardIconX = pw / 2 - 70;
    const badgeX = pw / 2 - 40;
    const badgeR = 9;

    const REWARD_ICONS: Record<string, { icon: string; color: number }> = {
      currency:    { icon: '\ud83d\udcb0', color: 0xffdd44 },
      electro:     { icon: '\u26a1', color: 0x44ddff },
      connardium:  { icon: '\ud83d\udc8e', color: 0xff44dd },
      perk:        { icon: '\ud83c\udf81', color: 0x88ff88 },
    };

    for (const cat of CATEGORY_ORDER) {
      const catAchievements = allProgress.filter(a => a.def.category === cat.key);
      if (catAchievements.length === 0) continue;

      const catSep = this.scene.add.graphics();
      catSep.lineStyle(1, 0x333355, 0.4);
      catSep.lineBetween(-pw / 2 + 20, cy, pw / 2 - 20, cy);
      content.add(catSep);
      cy += 10;

      content.add(this.scene.add.text(achieveLeft, cy, t(`achievementCat.${cat.key}`), {
        fontSize: '13px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0, 0.5));
      cy += 22;

      for (const prog of catAchievements) {
        const { def, currentValue, unlocked, isRevealed } = prog;
        const claimed = ProgressionManager.getAchievementClaimedReward(def.id) >= 1;

        const displayName = isRevealed ? t(`achievement.${def.id}.name`) : t('achievement.hidden');
        const nameObj = this.scene.add.text(achieveLeft + 4, cy - 6, displayName, {
          fontSize: '13px', color: '#ffffff', fontFamily: 'monospace', fontStyle: unlocked ? 'bold' : 'normal',
        }).setOrigin(0, 0.5);
        content.add(nameObj);

        // Dev: click on name to force-unlock when achieve onclick mode is active
        if (devAchieveOnClick && !unlocked) {
          nameObj.setInteractive({ useHandCursor: true });
          nameObj.setColor('#ff6666');
          nameObj.on('pointerover', () => nameObj.setColor('#ff4444'));
          nameObj.on('pointerout', () => nameObj.setColor('#ff6666'));
          nameObj.on('pointerdown', () => {
            ProgressionManager.setAchievementTier(def.id, 1);
            EventBus.emit(GameEvents.ACHIEVEMENT_UNLOCKED, { id: def.id, badge: def.badge, def });
            SoundManager.play('click');
            rebuildContent?.(true);
          });
        }

        // Show reward breakdown with icons next to name when unlocked
        if (unlocked && isRevealed) {
          const rewards = AchievementManager.getRewards(def);
          let rx = achieveLeft + 4 + nameObj.width + 10;
          const ricoSz = 12;
          const rColor = claimed ? '#446655' : '#88cc66';
          for (const r of rewards) {
            const texMap: Record<string, string> = { currency: 'icon_coin', connardium: 'icon_co', electro: 'icon_electro' };
            const tex = texMap[r.type];
            if (tex && r.amount && this.scene.textures.exists(tex)) {
              content.add(this.scene.add.image(rx + ricoSz / 2, cy - 6, tex).setDisplaySize(ricoSz, ricoSz));
              rx += ricoSz + 2;
              const val = this.scene.add.text(rx, cy - 6, `+${r.amount}`, {
                fontSize: '10px', color: rColor, fontFamily: 'monospace',
              }).setOrigin(0, 0.5);
              content.add(val);
              rx += val.width + 6;
            } else if (r.type === 'perk') {
              content.add(this.scene.add.text(rx, cy - 6, 'Perk', {
                fontSize: '10px', color: rColor, fontFamily: 'monospace',
              }).setOrigin(0, 0.5));
            }
          }
        }

        const descText = isRevealed ? t(`achievement.${def.id}.desc`) : t('achievement.hidden');
        content.add(this.scene.add.text(achieveLeft + 4, cy + 10, descText, {
          fontSize: '9px', color: '#555577', fontFamily: 'monospace',
        }).setOrigin(0, 0.5));

        const barX = pw / 2 - 220;
        const barW = 100, barH = 6;
        if (isRevealed) {
          const targetVal = def.threshold;
          const progressStr = def.unit
            ? `${currentValue}${def.unit} / ${targetVal}${def.unit}`
            : `${currentValue} / ${targetVal}`;
          const ratio = Math.min(1, currentValue / targetVal);
          const barGfx = this.scene.add.graphics();
          barGfx.fillStyle(0x222244, 0.6);
          barGfx.fillRoundedRect(barX, cy - barH / 2, barW, barH, 2);
          const fillColor = unlocked ? TIER_COLORS[def.badge] : 0x4488aa;
          barGfx.fillStyle(fillColor, 0.7);
          barGfx.fillRoundedRect(barX, cy - barH / 2, Math.max(2, barW * ratio), barH, 2);
          content.add(barGfx);

          content.add(this.scene.add.text(barX + barW / 2, cy + barH / 2 + 4, progressStr, {
            fontSize: '8px', color: '#888899', fontFamily: 'monospace',
          }).setOrigin(0.5, 0));
        }

        // Reward icon
        if (unlocked && isRevealed) {
          const rewards = AchievementManager.getRewards(def);
          const primaryReward = rewards[0];
          const ri = REWARD_ICONS[primaryReward.type] ?? REWARD_ICONS.currency;

          const rewardText = this.scene.add.text(rewardIconX, cy, ri.icon, {
            fontSize: '14px', fontFamily: 'monospace',
          }).setOrigin(0.5, 0.5);
          content.add(rewardText);

          if (!claimed) {
            rewardText.setInteractive({ useHandCursor: true });
            this.scene.tweens.add({
              targets: rewardText,
              scaleX: { from: 1, to: 1.3 }, scaleY: { from: 1, to: 1.3 },
              alpha: { from: 1, to: 0.6 },
              yoyo: true, repeat: -1, duration: 600,
            });
            rewardText.on('pointerdown', () => {
              SoundManager.play('click');
              this.openRewardReveal(panel, def.id, rewards, rebuildContent);
            });
          } else {
            rewardText.setAlpha(0.35);
          }
        }

        // Badge circle
        const gfx = this.scene.add.graphics();
        const color = TIER_COLORS[def.badge];
        if (unlocked) {
          gfx.fillStyle(color, 0.9);
          gfx.fillCircle(badgeX, cy, badgeR);
          gfx.lineStyle(1.5, 0xffffff, 0.4);
          gfx.strokeCircle(badgeX, cy, badgeR);
          if (def.badge === 'gold' || def.badge === 'platinum') {
            gfx.lineStyle(2, color, 0.35);
            gfx.strokeCircle(badgeX, cy, badgeR + 4);
          }
          if (def.badge === 'platinum') {
            gfx.lineStyle(1, 0xffffff, 0.2);
            gfx.strokeCircle(badgeX, cy, badgeR + 7);
          }
          if (def.badge === 'gold' || def.badge === 'platinum') {
            gfx.fillStyle(0xffffff, 0.6);
            gfx.fillCircle(badgeX, cy, 2);
          }
        } else {
          gfx.lineStyle(1.5, color, 0.25);
          gfx.strokeCircle(badgeX, cy, badgeR);
          gfx.fillStyle(0x222233, 0.4);
          gfx.fillCircle(badgeX, cy, badgeR);
        }
        content.add(gfx);

        cy += 44;
      }
    }

    const contentTotalH = (cy + 10) - viewTop;
    return Math.max(0, contentTotalH - viewH);
  }

  /** Build the Collection tab -- gallery of unlocked rewards. Returns maxScroll. */
  private buildCollectionTab(content: Phaser.GameObjects.Container, pw: number, viewTop: number, viewH: number): number {
    let cy = viewTop + 16;
    const unlockedPerks = ProgressionManager.getUnlockedPerks();
    const categories: PerkCategory[] = ['music', 'title_screen', 'art', 'dev_video'];

    // Gallery-style card dimensions
    const cardW = 120, cardH = 100, cardGap = 14;
    const cardsPerRow = Math.min(4, Math.floor((pw - 60) / (cardW + cardGap)));

    // Category accent colors
    const CAT_COLORS: Record<PerkCategory, number> = {
      music: 0x44aaff, title_screen: 0xffaa44, art: 0xff66aa, dev_video: 0x66ff88,
    };

    for (const cat of categories) {
      const catPerks = PERK_DEFS.filter(p => p.category === cat);
      const unlockedInCat = catPerks.filter(p => unlockedPerks.includes(p.id));
      const icon = PERK_CATEGORY_ICONS[cat];
      const label = PERK_CATEGORY_LABELS[cat];
      const accent = CAT_COLORS[cat];

      // ── Shelf header -- decorative line + icon + label ──
      const shelfGfx = this.scene.add.graphics();
      shelfGfx.lineStyle(2, accent, 0.5);
      shelfGfx.lineBetween(-pw / 2 + 24, cy, pw / 2 - 24, cy);
      content.add(shelfGfx);
      cy += 16;

      content.add(this.scene.add.text(-pw / 2 + 30, cy, `${icon}  ${label.toUpperCase()}`, {
        fontSize: '13px', color: `#${accent.toString(16).padStart(6, '0')}`, fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0, 0.5));

      // Counter (right-aligned, subtle)
      if (catPerks.length > 0) {
        content.add(this.scene.add.text(pw / 2 - 30, cy, `${unlockedInCat.length} / ${catPerks.length}`, {
          fontSize: '10px', color: '#555577', fontFamily: 'monospace',
        }).setOrigin(1, 0.5));
      }
      cy += 24;

      // ── Gallery grid ──
      if (catPerks.length > 0) {
        const gridW = cardsPerRow * (cardW + cardGap) - cardGap;
        let col = 0;
        const rowX = -gridW / 2;

        for (const perk of catPerks) {
          const isUnlocked = unlockedPerks.includes(perk.id);
          const cx = rowX + col * (cardW + cardGap);

          const g = this.scene.add.graphics();

          if (isUnlocked) {
            // ── Unlocked card: golden frame, rich background ──
            // Inner fill
            g.fillStyle(0x141428, 0.95);
            g.fillRoundedRect(cx, cy, cardW, cardH, 6);
            // Gold border
            g.lineStyle(2, accent, 0.8);
            g.strokeRoundedRect(cx, cy, cardW, cardH, 6);
            // Subtle inner glow line
            g.lineStyle(1, accent, 0.15);
            g.strokeRoundedRect(cx + 3, cy + 3, cardW - 6, cardH - 6, 4);
          } else {
            // ── Locked card: dark silhouette, dashed-style border ──
            g.fillStyle(0x08080f, 0.9);
            g.fillRoundedRect(cx, cy, cardW, cardH, 6);
            g.lineStyle(1, 0x222233, 0.4);
            g.strokeRoundedRect(cx, cy, cardW, cardH, 6);
          }
          content.add(g);

          // ── Card content ──
          if (isUnlocked) {
            // Large category icon
            content.add(this.scene.add.text(cx + cardW / 2, cy + 24, icon, {
              fontSize: '26px', fontFamily: 'monospace',
            }).setOrigin(0.5, 0.5));

            // Perk name (bold, centered)
            content.add(this.scene.add.text(cx + cardW / 2, cy + 48, perk.name, {
              fontSize: '9px', color: '#ddddee', fontFamily: 'monospace', fontStyle: 'bold',
              wordWrap: { width: cardW - 12 }, align: 'center',
            }).setOrigin(0.5, 0));

            // Short description (1 line max, truncated)
            content.add(this.scene.add.text(cx + cardW / 2, cy + 68, perk.description, {
              fontSize: '7px', color: '#667788', fontFamily: 'monospace',
              wordWrap: { width: cardW - 8 }, align: 'center',
              maxLines: 2,
            }).setOrigin(0.5, 0));
          } else {
            // Lock icon
            content.add(this.scene.add.text(cx + cardW / 2, cy + 24, '\ud83d\udd12', {
              fontSize: '22px', fontFamily: 'monospace',
            }).setOrigin(0.5, 0.5).setAlpha(0.25));

            // Mystery label
            content.add(this.scene.add.text(cx + cardW / 2, cy + 50, '???', {
              fontSize: '11px', color: '#222244', fontFamily: 'monospace', fontStyle: 'bold',
            }).setOrigin(0.5, 0));

            // Hint: how to unlock
            content.add(this.scene.add.text(cx + cardW / 2, cy + 70, perk.hint, {
              fontSize: '7px', color: '#1a1a33', fontFamily: 'monospace', fontStyle: 'italic',
              wordWrap: { width: cardW - 8 }, align: 'center',
              maxLines: 2,
            }).setOrigin(0.5, 0));
          }

          col++;
          if (col >= cardsPerRow) {
            col = 0;
            cy += cardH + cardGap;
          }
        }
        if (col > 0) cy += cardH + cardGap; // finish partial row
        cy += 10;
      } else {
        // Empty shelf -- "coming soon" with decorative dots
        const emptyGfx = this.scene.add.graphics();
        emptyGfx.lineStyle(1, 0x222233, 0.3);
        for (let dx = -80; dx <= 80; dx += 20) {
          emptyGfx.fillStyle(0x222233, 0.3);
          emptyGfx.fillCircle(dx, cy + 20, 2);
        }
        content.add(emptyGfx);

        content.add(this.scene.add.text(0, cy + 20, `${icon}  Coming soon...`, {
          fontSize: '11px', color: '#333355', fontFamily: 'monospace', fontStyle: 'italic',
        }).setOrigin(0.5, 0.5));
        cy += 50;
      }
    }

    const contentTotalH = (cy + 10) - viewTop;
    return Math.max(0, contentTotalH - viewH);
  }

  /** Reward reveal mini-panel -- gift-open animation + collect button */
  private openRewardReveal(parentPanel: Phaser.GameObjects.Container, achievementId: string, rewards: AchievementReward[], rebuildContent?: (keepScroll?: boolean) => void): void {
    const rpw = 300, rph = 220;
    const revealPanel = this.scene.add.container(0, 0).setDepth(30);
    parentPanel.add(revealPanel);

    // Dark overlay within panel
    const overlay = this.scene.add.rectangle(0, 0, 800, 800, 0x000000, 0.5).setInteractive();
    revealPanel.add(overlay);

    // Panel bg
    const rbg = this.scene.add.graphics();
    rbg.fillStyle(0x0c0c22, 0.97);
    rbg.fillRoundedRect(-rpw / 2, -rph / 2, rpw, rph, 10);
    rbg.lineStyle(2, 0xffaa44, 0.7);
    rbg.strokeRoundedRect(-rpw / 2, -rph / 2, rpw, rph, 10);
    revealPanel.add(rbg);

    // Block click-through
    revealPanel.add(this.scene.add.rectangle(0, 0, rpw, rph, 0x000000, 0).setInteractive());

    // Reward type -> texture key + color
    const REVEAL_ICONS: Record<string, { tex: string; color: string; format: (r: AchievementReward) => string }> = {
      currency:    { tex: 'icon_coin',    color: '#ffdd44', format: r => `+${r.amount ?? 0}` },
      electro:     { tex: 'icon_electro', color: '#44ddff', format: r => `+${r.amount ?? 0}` },
      connardium:  { tex: 'icon_co',      color: '#ff44dd', format: r => `+${r.amount ?? 0}` },
      perk:        { tex: '',             color: '#88ff88', format: r => r.label ?? 'Perk Unlocked!' },
    };

    // Gift icon -- animate scale up
    const giftIcon = this.scene.add.text(0, -rph / 2 + 50, '\ud83c\udf81', {
      fontSize: '40px', fontFamily: 'monospace',
    }).setOrigin(0.5, 0.5).setScale(0);
    revealPanel.add(giftIcon);

    this.scene.tweens.add({
      targets: giftIcon,
      scaleX: 1, scaleY: 1,
      duration: 400, ease: 'Back.easeOut',
    });

    // Reward list
    let ry = -10;
    for (const r of rewards) {
      const rd = REVEAL_ICONS[r.type] ?? REVEAL_ICONS.currency;
      // Icon image + text side by side
      const icoSz = 22;
      if (rd.tex && this.scene.textures.exists(rd.tex)) {
        const ico = this.scene.add.image(-40, ry, rd.tex).setDisplaySize(icoSz, icoSz).setAlpha(0);
        revealPanel.add(ico);
        this.scene.tweens.add({ targets: ico, alpha: 1, y: ry - 5, duration: 300, delay: 400, ease: 'Power2' });
      }
      const txt = this.scene.add.text(rd.tex ? -20 : 0, ry, rd.format(r), {
        fontSize: '16px', color: rd.color, fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(rd.tex ? 0 : 0.5, 0.5).setAlpha(0);
      revealPanel.add(txt);

      // Fade in with delay
      this.scene.tweens.add({
        targets: txt, alpha: 1, y: ry - 5,
        duration: 300, delay: 400, ease: 'Power2',
      });
      ry += 28;
    }

    // Collect button
    const collectBtn = createTechButton(this.scene, 0, rph / 2 - 40, '\u2705 COLLECT', {
      onClick: () => {
        SoundManager.play('click');
        AchievementManager.claimReward(achievementId);
        // Quick flash effect
        const flash = this.scene.add.rectangle(0, 0, rpw, rph, 0xffffff, 0.3);
        revealPanel.add(flash);
        this.scene.tweens.add({ targets: flash, alpha: 0, duration: 300, onComplete: () => flash.destroy() });
        // Close after brief delay and refresh stats panel
        this.scene.time.delayedCall(400, () => {
          revealPanel.destroy();
          rebuildContent?.(true);
        });
      },
      width: 140, height: 36, fontSize: '14px',
    });
    revealPanel.add(collectBtn);

    // Click overlay to close without claiming
    overlay.on('pointerdown', () => {
      revealPanel.destroy();
    });
  }

  close(): void {
    this.statsOpen = false;
    if (this.statsPanel) {
      this.statsPanel.destroy();
      this.statsPanel = undefined;
    }
  }

  // ── Achievement notification (title screen, above STATS) ──

  showAchievementNotification(): void {
    if (this._achieveQueue.length === 0) { this._achieveNotifying = false; return; }
    this._achieveNotifying = true;
    const data = this._achieveQueue.shift()!;
    const def = data.def;
    const tierColor = TIER_COLORS[data.badge as keyof typeof TIER_COLORS] ?? 0xffd700;
    const tierColorStr = `#${tierColor.toString(16).padStart(6, '0')}`;

    const cx = CANVAS_WIDTH / 2, cy = CANVAS_HEIGHT / 2;
    const W = 340, H = 180;

    // Depth 50 = above STATS panel (depth 25) and everything else
    const container = this.scene.add.container(cx, cy).setDepth(50).setScale(0);

    // Dim backdrop
    const backdrop = this.scene.add.rectangle(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.5)
      .setInteractive();
    container.add(backdrop);

    // Panel
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0a0a1e, 0.95);
    bg.fillRoundedRect(-W / 2, -H / 2, W, H, 10);
    bg.lineStyle(2, tierColor, 0.8);
    bg.strokeRoundedRect(-W / 2, -H / 2, W, H, 10);
    container.add(bg);

    // Badge circle
    const badgeGfx = this.scene.add.graphics();
    badgeGfx.fillStyle(tierColor, 1);
    badgeGfx.fillCircle(0, -H / 2, 18);
    badgeGfx.lineStyle(2, 0xffffff, 0.4);
    badgeGfx.strokeCircle(0, -H / 2, 18);
    container.add(badgeGfx);
    container.add(this.scene.add.text(0, -H / 2, '\ud83c\udfc5', {
      fontSize: '18px', fontFamily: 'monospace',
    }).setOrigin(0.5, 0.5));

    // Header
    container.add(this.scene.add.text(0, -H / 2 + 28, 'ACHIEVEMENT UNLOCKED', {
      fontSize: '10px', color: tierColorStr, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0.5));

    // Name
    container.add(this.scene.add.text(0, -H / 2 + 52, t(`achievement.${def.id}.name`), {
      fontSize: '16px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
      wordWrap: { width: W - 40 }, align: 'center',
    }).setOrigin(0.5, 0.5));

    // Description
    container.add(this.scene.add.text(0, -H / 2 + 76, t(`achievement.${def.id}.desc`), {
      fontSize: '10px', color: '#8899aa', fontFamily: 'monospace',
      wordWrap: { width: W - 40 }, align: 'center',
    }).setOrigin(0.5, 0.5));

    // Reward hint
    container.add(this.scene.add.text(0, -H / 2 + 100, '\ud83c\udf81 Open STATS to claim your reward!', {
      fontSize: '10px', color: '#ffcc44', fontFamily: 'monospace',
    }).setOrigin(0.5, 0.5));

    // OK button
    const btnW = 80, btnH = 30;
    const btnY = H / 2 - 28;
    const btnGfx = this.scene.add.graphics();
    btnGfx.fillStyle(tierColor, 0.8);
    btnGfx.fillRoundedRect(-btnW / 2, btnY - btnH / 2, btnW, btnH, 5);
    container.add(btnGfx);
    container.add(this.scene.add.text(0, btnY, 'OK', {
      fontSize: '14px', color: '#000000', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0.5));

    const btnHit = this.scene.add.rectangle(0, btnY, btnW, btnH, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    btnHit.on('pointerover', () => btnGfx.clear().fillStyle(tierColor, 1).fillRoundedRect(-btnW / 2, btnY - btnH / 2, btnW, btnH, 5));
    btnHit.on('pointerout', () => btnGfx.clear().fillStyle(tierColor, 0.8).fillRoundedRect(-btnW / 2, btnY - btnH / 2, btnW, btnH, 5));
    btnHit.on('pointerdown', () => {
      SoundManager.play('click');
      container.destroy();
      this.showAchievementNotification(); // process next in queue
    });
    container.add(btnHit);

    // Pop-in
    this.scene.tweens.add({
      targets: container,
      scaleX: 1, scaleY: 1,
      duration: 350, ease: 'Back.easeOut',
    });

    SoundManager.play('click');
  }
}
